/**
 * 帐号详情 › 各「相关页签」的词条：登录日志、绑定的属会、提交的申请、产生的订单。
 *
 * 单独一份、由 `locale-resources.ts` 并进主语言包，而不是塞进 `locales/zh-CN.ts` 那几个
 * 上千行的文件：那几个文件是三个语言一起改的热点，新加一组词条时容易和别人的改动撞行。
 *
 * 字段名照后端契约原样：登录日志读 iam 的 `LoginLogRow`，属会读 `ClubAdminGrantRow`，
 * 申请读 `ApplicationRecordResp`，订单读 billing `BillingOrderRow`。
 */
export default {
  'acct.portal.loginLog.entryCount': '{name} {count}次',
  // ── 登录日志（iam API-10-013）──
  // `endName` 是「从哪儿登的」，`entry` 是「进的是哪个入口」——用户侧只有官网一个端，
  // 把空间当成端记，同一次登录会被数成两三次。
  'acct.portal.loginLog.title': '登录日志（{count}）',
  'acct.portal.loginLog.col.at': '时间',
  'acct.portal.loginLog.col.end': '从哪儿登的',
  'acct.portal.loginLog.col.ip': 'IP',
  'acct.portal.loginLog.col.device': '设备',
  'acct.portal.loginLog.col.result': '结果',
  'acct.portal.loginLog.ok': '成功',
  'acct.portal.loginLog.fail': '失败',
  'acct.portal.loginLog.enter': '进入：{space}',
  'acct.portal.loginLog.entry.site': '官网入口',
  'acct.portal.loginLog.entry.member': '会员入口',
  'acct.portal.loginLog.entry.club': '属会入口',
  'acct.portal.loginLog.empty': '暂无登录记录',

  // ── 绑定的属会（member clubAdminGrant）：授权的是属会，与会内人员绑定是两个维度 ──
  'acct.portal.related.club.title': '绑定的属会（{count}）',
  'acct.portal.related.club.name': '属会',
  'acct.portal.related.club.status': '状态',
  'acct.portal.related.club.validity': '有效期',
  'acct.portal.related.club.granted': '授予',
  'acct.portal.related.club.noExpiry': '不设到期日',
  'acct.portal.related.club.empty': '本帐号没有被授权管理任何属会',

  // ── 提交的申请（member API-11-007 的发起帐号档）：只列本帐号自己提交的 ──
  'acct.portal.related.application.title': '提交的申请（{count}）',
  'acct.portal.related.application.no': '编号',
  'acct.portal.related.application.type': '类型',
  'acct.portal.related.application.onBehalfOf': '替谁办',
  'acct.portal.related.application.status': '状态',
  'acct.portal.related.application.submittedAt': '提交时间',
  'acct.portal.related.application.empty': '暂无申请记录',

  // ── 产生的订单（billing API-15-001）：只列本帐号自己下的单 ──
  'acct.portal.related.order.title': '产生的订单（{count}）',
  'acct.portal.related.order.no': '订单号',
  'acct.portal.related.order.type': '类型',
  'acct.portal.related.order.amount': '金额',
  'acct.portal.related.order.beneficiary': '这笔钱花在谁身上',
  'acct.portal.related.order.status': '状态',
  'acct.portal.related.order.empty': '本帐号没有下过任何订单',

  // ── 列表批量（原型 ADM.apBulkBar / apBulkGo）：停用 / 解除停用 / 发激活邀请 ──
  // 「发激活邀请」发的是该帐号绑定的「本人」；没有本人绑定的行标未办成。
  'acct.portal.bulk.selected': '已选 {count} 个',
  'acct.portal.bulk.disable': '批量停用',
  'acct.portal.bulk.enable': '批量解除停用',
  'acct.portal.bulk.invite': '批量发激活邀请',
  'acct.portal.bulk.clear': '清空勾选',
  'acct.portal.bulk.run': '开始执行',
  'acct.portal.bulk.rowByRow':
    '逐行校验、逐行给结果：办不了的那几行会写清原因，不拦住其余的。判定与一行一行点完全一样。',
  'acct.portal.bulk.inviteScope':
    '激活邀请发给这些帐号绑定的「本人」；没有绑定「本人」的行会标出来。',
  'acct.portal.bulk.reasonLabel': '原因 / 说明',
  'acct.portal.bulk.reasonOptional': '选填，写进审计',
  'acct.portal.bulk.reasonRequired': '批量停用要填原因 —— 用户看到的拦截提示就是它',
  'acct.portal.bulk.inviteNoSelf': '该帐号没有绑定「本人」，没有可发邀请的对象',
  'acct.portal.bulk.inviteSkipped': '未发出',
  'acct.portal.bulk.noVersion': '这一行缺少版本号，请刷新后重试',
  'acct.portal.bulk.nothingSelected': '请先勾选',
  'acct.portal.bulk.requestFailed': '这一批没有办成',
  'acct.portal.bulk.resultTitle': '批量操作 · 结果',
  'acct.portal.bulk.resultSummary':
    '共 {total} 行：成功 {ok} 行，未办成 {bad} 行。已办成的行不需要重做。',
  'acct.portal.bulk.exportResult': '导出结果',
  'acct.portal.bulk.colAccount': '帐号',
  'acct.portal.bulk.colResult': '结果',
  'acct.portal.bulk.colNote': '说明',
  'acct.portal.bulk.ok': '已完成',
  'acct.portal.bulk.failed': '未办成',
  'acct.req.col.subject': '申请事项',
  'acct.portal.detail.realNameUnbound': '未绑「本人」，不涉及',
  'acct.portal.detail.realNameVoucher': '核验凭证号',
  'acct.portal.contact.maintain': '维护联系人',
  'acct.portal.contact.create': '留一组联系人',
  'acct.portal.contact.title': '帐号联系人 · {login}',
  'acct.portal.contact.phone': '联系电话',
  'acct.portal.contact.emailPlaceholder': '留空则用登录邮箱',
  'acct.portal.contact.noSelfNotice': '这个帐号没有「本人」绑定 —— 持有人不是本会的会内人员。本会不为他建档：建了会把他算进人数与配额，而他并不是本会的人。这里留一组联络方式，够发信与联系即可。',
  'acct.portal.col.holderContact': '帐号联系人 {name}',
} as const;
